import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckCircle, XCircle, AlertTriangle, User, Calendar, 
  Users, Clock, Home, RefreshCw
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PantryCheckinPage() {
  const { registrationId } = useParams();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing'); // processing, success, already_checked_in, wrong_date, not_found, error
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (registrationId) {
      processCheckin();
    }
  }, [registrationId]);

  const processCheckin = async () => {
    setStatus('processing');
    
    try {
      // First verify the registration
      const verifyResponse = await axios.get(`${API}/pantry/verify/${registrationId}`);
      const verifyData = verifyResponse.data;

      if (!verifyData.valid) {
        setStatus(verifyData.status || 'error');
        setData({
          guestName: verifyData.guest_name,
          appointmentDate: verifyData.appointment_date,
          appointmentTime: verifyData.appointment_time,
          message: verifyData.message,
          checkedInBy: verifyData.checked_in_by,
          checkedInAt: verifyData.checked_in_at
        });
        return;
      }

      // Perform check-in (use URL-based check-in which doesn't require staff PIN)
      const checkInResponse = await axios.post(`${API}/pantry/check-in-url`, {
        registration_id: registrationId
      });

      if (checkInResponse.data.status === 'success') {
        setStatus('success');
        setData({
          guestName: checkInResponse.data.guest_name,
          householdSize: checkInResponse.data.household_size,
          appointmentDate: verifyData.appointment_date,
          appointmentTime: verifyData.appointment_time,
          checkedInAt: checkInResponse.data.checked_in_at,
          message: checkInResponse.data.message
        });
      } else {
        setStatus(checkInResponse.data.status || 'error');
        setData({
          guestName: checkInResponse.data.guest_name,
          message: checkInResponse.data.message
        });
      }

    } catch (error) {
      console.error('Check-in error:', error);
      if (error.response?.status === 404) {
        setStatus('not_found');
        setError('Registration not found. Please check the QR code and try again.');
      } else {
        setStatus('error');
        setError(error.response?.data?.detail || 'An error occurred during check-in. Please try again.');
      }
    }
  };

  // Processing state
  if (status === 'processing') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-600 to-green-800 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-sm text-center"
        >
          <div className="w-20 h-20 mx-auto mb-6 relative">
            <div className="absolute inset-0 border-4 border-green-200 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-green-600 rounded-full border-t-transparent animate-spin"></div>
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Processing Check-In</h2>
          <p className="text-gray-500">Please wait...</p>
        </motion.div>
      </div>
    );
  }

  // Success state
  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-500 to-green-700 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-sm text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", delay: 0.2 }}
            className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle className="text-green-600" size={56} />
          </motion.div>

          <h1 className="text-3xl font-bold text-green-700 mb-2">Welcome!</h1>
          <p className="text-gray-600 mb-6">Check-in successful</p>

          <div className="bg-green-50 rounded-xl p-5 mb-6 text-left border border-green-200">
            <div className="flex items-center gap-3 mb-4">
              <User className="text-green-600" size={24} />
              <div>
                <p className="text-sm text-gray-500">Guest Name</p>
                <p className="font-bold text-gray-800 text-lg">{data?.guestName}</p>
              </div>
            </div>

            {data?.householdSize && (
              <div className="flex items-center gap-3 mb-4">
                <Users className="text-green-600" size={24} />
                <div>
                  <p className="text-sm text-gray-500">Household Size</p>
                  <p className="font-semibold text-gray-800">{data.householdSize} people</p>
                </div>
              </div>
            )}

            {data?.appointmentDate && (
              <div className="flex items-center gap-3 mb-4">
                <Calendar className="text-green-600" size={24} />
                <div>
                  <p className="text-sm text-gray-500">Appointment</p>
                  <p className="font-semibold text-gray-800">{data.appointmentDate}</p>
                  {data?.appointmentTime && (
                    <p className="text-sm text-gray-600">{data.appointmentTime}</p>
                  )}
                </div>
              </div>
            )}

            <div className="flex items-center gap-3">
              <Clock className="text-green-600" size={24} />
              <div>
                <p className="text-sm text-gray-500">Checked In At</p>
                <p className="font-semibold text-gray-800">
                  {data?.checkedInAt ? new Date(data.checkedInAt).toLocaleTimeString() : 'Just now'}
                </p>
              </div>
            </div>
          </div>

          <p className="text-green-700 font-medium text-lg">
            ✓ You may proceed to receive your items
          </p>
        </motion.div>
      </div>
    );
  }

  // Already checked in state
  if (status === 'already_checked_in') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-yellow-500 to-orange-500 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-sm text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", delay: 0.2 }}
            className="w-24 h-24 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <AlertTriangle className="text-yellow-600" size={56} />
          </motion.div>

          <h1 className="text-2xl font-bold text-yellow-700 mb-2">Already Checked In</h1>
          <p className="text-gray-600 mb-6">{data?.message || 'This QR code has already been used today.'}</p>

          <div className="bg-yellow-50 rounded-xl p-5 mb-6 text-left border border-yellow-200">
            {data?.guestName && (
              <div className="flex items-center gap-3 mb-4">
                <User className="text-yellow-600" size={24} />
                <div>
                  <p className="text-sm text-gray-500">Guest Name</p>
                  <p className="font-bold text-gray-800">{data.guestName}</p>
                </div>
              </div>
            )}

            {data?.checkedInBy && (
              <div className="flex items-center gap-3">
                <Clock className="text-yellow-600" size={24} />
                <div>
                  <p className="text-sm text-gray-500">Previously Checked In</p>
                  <p className="font-semibold text-gray-800">By: {data.checkedInBy}</p>
                  {data?.checkedInAt && (
                    <p className="text-sm text-gray-600">
                      At: {new Date(data.checkedInAt).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          <p className="text-yellow-700 font-medium">
            Each registration can only be used once per event date.
          </p>
        </motion.div>
      </div>
    );
  }

  // Wrong date state
  if (status === 'wrong_date') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-500 to-red-500 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-sm text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", delay: 0.2 }}
            className="w-24 h-24 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <Calendar className="text-orange-600" size={56} />
          </motion.div>

          <h1 className="text-2xl font-bold text-orange-700 mb-2">Wrong Date</h1>
          <p className="text-gray-600 mb-6">{data?.message || 'This registration is for a different date.'}</p>

          <div className="bg-orange-50 rounded-xl p-5 mb-6 text-left border border-orange-200">
            {data?.guestName && (
              <div className="flex items-center gap-3 mb-4">
                <User className="text-orange-600" size={24} />
                <div>
                  <p className="text-sm text-gray-500">Guest Name</p>
                  <p className="font-bold text-gray-800">{data.guestName}</p>
                </div>
              </div>
            )}

            {data?.appointmentDate && (
              <div className="flex items-center gap-3">
                <Calendar className="text-orange-600" size={24} />
                <div>
                  <p className="text-sm text-gray-500">Scheduled Date</p>
                  <p className="font-bold text-gray-800">{data.appointmentDate}</p>
                  {data?.appointmentTime && (
                    <p className="text-sm text-gray-600">{data.appointmentTime}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          <p className="text-orange-700 font-medium">
            Please come back on your scheduled date.
          </p>
        </motion.div>
      </div>
    );
  }

  // Not found or error state
  return (
    <div className="min-h-screen bg-gradient-to-b from-red-500 to-red-700 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-sm text-center"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", delay: 0.2 }}
          className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6"
        >
          <XCircle className="text-red-600" size={56} />
        </motion.div>

        <h1 className="text-2xl font-bold text-red-700 mb-2">
          {status === 'not_found' ? 'Not Found' : 'Error'}
        </h1>
        <p className="text-gray-600 mb-6">
          {error || data?.message || 'Unable to process this check-in.'}
        </p>

        <div className="space-y-3">
          <button
            onClick={processCheckin}
            className="w-full py-3 bg-gray-800 text-white rounded-xl font-semibold hover:bg-gray-900 transition-colors flex items-center justify-center gap-2"
          >
            <RefreshCw size={18} />
            Try Again
          </button>
          
          <a
            href="/"
            className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
          >
            <Home size={18} />
            Go to Homepage
          </a>
        </div>
      </motion.div>
    </div>
  );
}
